import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  Link,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RowSelectionState, createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { auth } from "../../core/auth";
import DataTable from "../../shared/components/DataTable/DataTable";
import useEndpointDataTable from "../../shared/components/DataTable/useEndpointDataTable";
import SingleDatePicker from "../../shared/components/DatePicker/SingleDatePicker";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import { useEntityLink } from "../../shared/hooks/useEntityLink";
import { PatientId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import DailyNoteAttestationModal from "./components/DailyNoteAttestationModal";
import DailyNoteDetailsDrawer from "./components/DailyNoteDetailsDrawer";
import {
  CONCERNING_OPTIONS,
  ConcerningFilter,
  DueTag,
  ESCALATION_STYLE,
  ListItem,
  PATHWAY_OPTIONS,
  PageBody,
  Pathway,
  Priority,
  ResultCount,
  SummaryCell,
} from "./dailyNotesShared";

type Filters = {
  status: PageBody["status"];
  priorities: Priority[] | null;
  reviewPathways: Pathway[] | null;
  concerning: ConcerningFilter | null;
  dueOnly: boolean;
  fromDate: LocalDate | null;
  toDate: LocalDate | null;
};

/**
 * The two ways to read one patient's notes.
 *
 * - `review`: the notes no attestation covers yet -- the nurse's work. Rows can be selected and
 *   signed, which is only offered here because only these notes can be.
 * - `all`: the patient's whole history, signed or not, read-only. The status filter is the way
 *   in to "just what was signed" or "just what is awaiting a second reviewer".
 */
export type DailyNotesTableMode = "review" | "all";

const HISTORY_STATUS_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Ready for review", value: "READY_FOR_REVIEW" },
  // Wire value unchanged: it is an API contract and matches the `cosigned_at` column.
  { label: "Awaiting 2nd reviewer", value: "AWAITING_COSIGNATURE" },
  { label: "Signed", value: "APPROVED" },
];

const columnHelper = createColumnHelper<ListItem>();

/**
 * One patient's daily notes, as a table.
 *
 * The patient is fixed by the caller: the patient view is what chooses who, so there is no
 * patient filter here, and an attestation -- which covers exactly one participant -- can never
 * be built from a mixed selection in this view.
 *
 * No default date window, unlike the list of all patients: one patient's notes are a bounded
 * set, and a window would hide exactly the oldest notes, which are the ones that are due. A nurse
 * who filtered the list by due notes and opened a patient would otherwise land on an empty table.
 */
export default function DailyNotesTable(props: {
  patientId: PatientId;
  mode: DailyNotesTableMode;
}) {
  const { api, mutations } = useApi();
  const entityLink = useEntityLink();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Both re-sends demand the signing permission on the server, so the buttons check the same
  // one: a visible button that answers 403 is worse than no button.
  const canResend = auth.isAllowedTo("sign_visit_daily_note_attestation");

  const refreshAfterResend = () => queryClient.invalidateQueries({ queryKey: ["visitDailyNotes"] });

  const resendIncident = useMutation({
    ...mutations.dailyNotes.resendCriticalIncident(),
    onSuccess: () => {
      toast({
        title: "Queued for Salesforce",
        description: "The incident will be sent again shortly.",
        status: "success",
        position: "top-right",
        isClosable: true,
      });
      void refreshAfterResend();
    },
    onError: (error) =>
      toast({
        title: "Could not send the incident again",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      }),
  });

  const resendDocare = useMutation({
    ...mutations.dailyNotes.requeueDocareUpload(),
    onSuccess: () => {
      toast({
        title: "Queued for DoCare",
        description: "The note will be sent to DoCare again shortly.",
        status: "success",
        position: "top-right",
        isClosable: true,
      });
      void refreshAfterResend();
    },
    onError: (error) =>
      toast({
        title: "Could not queue the DoCare upload",
        description: formatErrorResponse(error),
        status: "error",
        position: "top-right",
        isClosable: true,
        duration: 9000,
      }),
  });

  const isReview = props.mode === "review";

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [openNoteId, setOpenNoteId] = React.useState<ListItem["id"] | null>(null);
  const [isAttestationOpen, setIsAttestationOpen] = React.useState(false);

  const columns = React.useMemo(
    () => [
      // Only where there is something to do with a selection.
      ...(isReview
        ? [
            columnHelper.display({
              id: "_select",
              header: ({ table }) => (
                <Checkbox
                  isChecked={table.getIsAllPageRowsSelected()}
                  isIndeterminate={table.getIsSomePageRowsSelected()}
                  onChange={table.getToggleAllPageRowsSelectedHandler()}
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  isChecked={row.getIsSelected()}
                  isDisabled={!row.getCanSelect()}
                  onChange={row.getToggleSelectedHandler()}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor("visitInstanceId", {
        header: "Visit",
        cell: (info) => (
          <Link
            color="blue.600"
            onClick={(event) => {
              event.stopPropagation();
              entityLink.open({
                event,
                entity: { type: "VisitInstance", id: info.getValue() },
              });
            }}
          >
            {String(info.getValue())}
          </Link>
        ),
      }),
      columnHelper.accessor("visitStartTime", {
        header: "Visit date & time",
        cell: ({ row, getValue }) => (
          <Flex align="center" gap={2}>
            <Text whiteSpace="nowrap">{dateFormatter.toDateTime(getValue())}</Text>
            {/* Due only matters while a note still waits for review. */}
            {row.original.isDue && row.original.status === "READY_FOR_REVIEW" ? <DueTag /> : null}
          </Flex>
        ),
      }),
      columnHelper.accessor("caregiverName", {
        header: "Caregiver",
        cell: ({ row }) =>
          row.original.caregiverId === null || row.original.caregiverName === null ? (
            <Text color="gray.500">—</Text>
          ) : (
            <Link
              color="blue.600"
              whiteSpace="nowrap"
              onClick={(event) => {
                event.stopPropagation();
                const caregiverId = row.original.caregiverId;
                if (caregiverId !== null) {
                  entityLink.open({ event, entity: { type: "Caregiver", id: caregiverId } });
                }
              }}
            >
              {row.original.caregiverName}
            </Link>
          ),
      }),
      columnHelper.accessor("summary", {
        header: "Summary",
        cell: (info) => <SummaryCell note={info.row.original} />,
      }),
      columnHelper.accessor("noteCreatedAt", {
        header: "Note received",
        cell: (info) => (
          <Text whiteSpace="nowrap">{dateFormatter.toDateTime(info.getValue())}</Text>
        ),
      }),
      columnHelper.accessor("status", {
        header: "Status",
        // Two independent statuses share one column. A note whose concern the nurse
        // confirmed is both signed and awaiting escalation; the escalation is the
        // more specific thing to say about it, so it wins the cell. Folding them into
        // a single field instead would have made a signed note stop reading as signed.
        cell: ({ row }) => {
          const escalation = row.original.escalationStatus;

          if (escalation !== null) {
            const style = ESCALATION_STYLE[escalation];
            // Only these two can change by sending again; the server refuses the rest.
            const canSendAgain =
              canResend && (escalation === "FAILED" || escalation === "NEEDS_REVIEW");

            return (
              <Flex align="flex-start" direction="column" gap={1}>
                <Text color={style.color}>{style.label}</Text>
                {canSendAgain ? (
                  <Button
                    isLoading={
                      resendIncident.isPending && resendIncident.variables === row.original.id
                    }
                    size="xs"
                    variant="outline"
                    onClick={(event) => {
                      event.stopPropagation();
                      resendIncident.mutate(row.original.id);
                    }}
                  >
                    Send to Salesforce again
                  </Button>
                ) : null}
              </Flex>
            );
          }

          switch (row.original.status) {
            case "APPROVED":
              return <Text color="green.700">Signed</Text>;
            case "AWAITING_COSIGNATURE":
              return <Text color="purple.700">Awaiting 2nd reviewer</Text>;
            default:
              return <Text>Ready for review</Text>;
          }
        },
      }),
      columnHelper.accessor("docareStatus", {
        header: "DoCare",
        cell: ({ row }) => {
          const docare = row.original.docareStatus;

          // Nothing is queued before signing, so an unsigned note has nothing to report.
          if (docare === null) {
            return <Text color="gray.500">—</Text>;
          }

          if (docare === "FAILED") {
            return (
              <Flex align="flex-start" direction="column" gap={1}>
                <Text color="red.700">Failed</Text>
                {canResend ? (
                  <Button
                    isLoading={resendDocare.isPending && resendDocare.variables === row.original.id}
                    size="xs"
                    variant="outline"
                    onClick={(event) => {
                      event.stopPropagation();
                      resendDocare.mutate(row.original.id);
                    }}
                  >
                    Send again
                  </Button>
                ) : null}
              </Flex>
            );
          }

          return docare === "PUBLISHED" ? (
            <Text color="green.700">Sent</Text>
          ) : (
            <Text color="gray.600">{docare === "SENDING" ? "Sending" : "Queued"}</Text>
          );
        },
      }),
    ],
    [entityLink, isReview, canResend, resendIncident, resendDocare]
  );

  const { table, query, totalCount, filters, setFilter, metaButtonProps } = useEndpointDataTable({
    queryKey: ["visitDailyNotes", "page", "patient", props.patientId, props.mode],
    columns: columns,
    columnVisiblity: {
      storage: { key: `visits/dailyNotes/patient-${props.mode}`, version: 3 },
    },
    filters: {
      initialState: {
        status: isReview ? "READY_FOR_REVIEW" : "ALL",
        priorities: null,
        reviewPathways: null,
        concerning: null,
        dueOnly: false,
        fromDate: null,
        toDate: null,
      } satisfies Filters as Filters,
    },
    rowSelection: {
      state: rowSelection,
      onRowSelectionChange: setRowSelection,
      // The note's own id, so a selection survives paging and re-sorting. The
      // default index-based id would re-point it at whatever row lands there.
      getRowId: (row) => String(row.id),
    },
    fetchPage: async (request) => {
      const body: PageBody = {
        status: request.filters.status,
        officeId: null,
        patientId: props.patientId,
        priorities: request.filters.priorities ?? [],
        reviewPathways: request.filters.reviewPathways ?? [],
        concerning: request.filters.concerning,
        dueOnly: request.filters.dueOnly,
        fromDate: request.filters.fromDate,
        toDate: request.filters.toDate,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        limit: request.limit,
        offset: request.offset,
      };

      return await api.post("./visit_daily_notes/page", { body });
    },
    // Serving one page at a time is the point of the endpoint; an export that
    // walked every page would undo it. Left out until someone asks.
    fetchAllForExport: async () => [],
  });

  // A selection is a list of notes in a particular state. Once the filters move,
  // that list no longer describes what is on screen.
  React.useEffect(() => {
    setRowSelection({});
  }, [filters]);

  const selectedNotes = table.getSelectedRowModel().rows.map((row) => row.original);

  const filterNode = (
    <Flex gap={3} wrap="wrap">
      {isReview ? null : (
        <Select
          label="Status"
          multiple={false}
          options={HISTORY_STATUS_OPTIONS}
          value={filters.status}
          onChange={(x) => setFilter("status", (x ?? "ALL") as Filters["status"])}
        />
      )}

      {/* Two fields rather than one range control. Either end stands on its own --
          "anything from the 1st" and "anything up to the 8th" are both useful -- and
          the endpoint already treats each end as independently nullable. */}
      <SingleDatePicker
        placeholderText="Visit from"
        selected={filters.fromDate}
        onChange={(date) => setFilter("fromDate", date)}
      />

      <SingleDatePicker
        placeholderText="Visit to"
        selected={filters.toDate}
        onChange={(date) => setFilter("toDate", date)}
      />

      <Select
        label="Pathway"
        multiple={true}
        options={PATHWAY_OPTIONS}
        value={filters.reviewPathways}
        onChange={(x) => setFilter("reviewPathways", (x as Pathway[] | null) ?? null)}
      />

      <Select
        allowUnselect={true}
        label="Concerning"
        multiple={false}
        options={CONCERNING_OPTIONS}
        value={filters.concerning}
        onChange={(x) => setFilter("concerning", x ?? null)}
      />

      <Checkbox
        alignSelf="center"
        isChecked={filters.dueOnly}
        onChange={(event) => setFilter("dueOnly", event.target.checked)}
      >
        Due only
      </Checkbox>
    </Flex>
  );

  // An attestation covers one participant -- the declaration is written in the singular and
  // the record is about one person's care. This view only ever shows one patient's notes, so
  // the selection cannot span patients; the check stays as a backstop because the server
  // refuses such a batch and finding out here is cheaper than after filling in rulings.
  const selectedPatientIds = new Set(selectedNotes.map((note) => String(note.patientId)));
  const spansPatients = selectedPatientIds.size > 1;

  const actionBlockedReason =
    selectedNotes.length === 0
      ? "Select at least one visit to create an attestation for"
      : spansPatients
      ? `An attestation covers one participant. These notes span ${selectedPatientIds.size} patients — sign them separately.`
      : null;

  // Always rendered, disabled until something is selected, rather than appearing only once a
  // row is ticked. A control that materialises out of nowhere gives no hint that selecting
  // rows is what unlocks it; a disabled one with a reason does.
  const actionNode = isReview ? (
    // No `size`: the meta buttons beside this one are default-size icon buttons, and a
    // smaller button here sits short against them.
    <ButtonGroup>
      <Tooltip
        // `shouldWrapChildren` is required: a disabled button emits no pointer events, so
        // without the wrapper the tooltip never fires -- which is precisely the state the
        // tooltip exists to explain.
        shouldWrapChildren
        isDisabled={actionBlockedReason === null}
        label={actionBlockedReason ?? ""}
      >
        <Button
          colorScheme="blue"
          isDisabled={actionBlockedReason !== null}
          onClick={() => setIsAttestationOpen(true)}
        >
          RN Attestation{selectedNotes.length === 0 ? "" : ` (${selectedNotes.length})`}
        </Button>
      </Tooltip>
    </ButtonGroup>
  ) : undefined;

  return (
    <Box>
      <Box mb={2}>
        <ResultCount count={totalCount} isLoading={query.isPending} noun="note" />
      </Box>

      <DataTable
        actionNode={actionNode}
        filterNode={filterNode}
        isLoading={query.isPending}
        metaButtonProps={metaButtonProps}
        spacing="tight"
        table={table}
        onClickRow={(_event, row) => setOpenNoteId(row.original.id)}
      />

      <DailyNoteDetailsDrawer
        visitInstanceDailyNoteId={openNoteId}
        onClose={() => setOpenNoteId(null)}
      />

      <DailyNoteAttestationModal
        isOpen={isAttestationOpen}
        notes={selectedNotes}
        onClose={() => setIsAttestationOpen(false)}
        onSigned={() => {
          setIsAttestationOpen(false);
          setRowSelection({});
        }}
      />
    </Box>
  );
}
